import { API_ENDPOINTS } from '../constants';
import { apiClient } from './apiClient';

function buildQuery(params: Record<string, any>): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
  }
  const s = qs.toString();
  return s ? `?${s}` : '';
}

export type FeatureType = 'boolean' | 'limit' | 'option';
export type FeatureCategory = 'access' | 'capacity' | 'workflow' | 'reporting' | 'support' | 'integration';
export type FeatureStatus = 'active' | 'archived';

export type OptionItem = {
  value: string;
  label: string;
  sort: number;
};

export type PodUsage = {
  podId: string;
  podName: string;
  version: string;
  status: 'active' | 'deleted';
};

export type PlanUsage = {
  value: boolean | number | string;
  podId: string;
  podName: string;
  planId: string;
  planKey: string;
  planName: string;
};

export type AuditLogEntry = {
  id: string;
  featureId: string;
  action: 'created' | 'updated' | 'archived' | 'restored' | 'deleted';
  actorId: string;
  actorEmail: string;
  timestamp: string;
  ipAddress?: string | null;
  changes: Record<string, { from: unknown; to: unknown }> | { record: unknown };
};

export type AuditLogResult = {
  logs: AuditLogEntry[];
  total: number;
};

export type Feature = {
  id: string;
  key: string;
  name: string;
  description?: string | null;
  type: FeatureType;
  unit?: string | null;
  unlimitedAllowed?: boolean | null;
  minValue?: number | null;
  maxValue?: number | null;
  options?: OptionItem[] | null;
  defaultValue: boolean | number | string;
  category: FeatureCategory;
  enforcedBy: string;
  displayOrder?: number | null;
  showOnPricingPage: boolean;
  status: FeatureStatus;
  createdBy?: string | null;
  updatedBy?: string | null;
  createdAt: string;
  updatedAt: string;
  archivedAt?: string | null;
  podCount: number;
};

export type FeatureDetail = Feature & {
  podsUsing: PodUsage[];
  planUsage: PlanUsage[];
  lockedFields: string[];
};

export type FeatureListResult = {
  features: Feature[];
  total: number;
};

export type FeatureListParams = {
  search?: string;
  type?: FeatureType;
  category?: FeatureCategory;
  status?: FeatureStatus;
  used?: boolean;
  page?: number;
  page_size?: number;
};

export type CreateFeaturePayload =
  | {
      type: 'boolean';
      name: string;
      key?: string;
      description?: string;
      defaultValue: boolean;
      category: FeatureCategory;
      enforcedBy: string;
      displayOrder?: number;
      showOnPricingPage?: boolean;
    }
  | {
      type: 'limit';
      name: string;
      key?: string;
      description?: string;
      defaultValue: number;
      unit: string;
      unlimitedAllowed?: boolean;
      minValue?: number;
      maxValue?: number;
      category: FeatureCategory;
      enforcedBy: string;
      displayOrder?: number;
      showOnPricingPage?: boolean;
    }
  | {
      type: 'option';
      name: string;
      key?: string;
      description?: string;
      defaultValue: string;
      options: OptionItem[];
      category: FeatureCategory;
      enforcedBy: string;
      displayOrder?: number;
      showOnPricingPage?: boolean;
    };

export type UpdateFeaturePayload = {
  name?: string;
  description?: string | null;
  defaultValue?: boolean | number | string;
  unit?: string;
  unlimitedAllowed?: boolean;
  minValue?: number | null;
  maxValue?: number | null;
  options?: OptionItem[];
  category?: FeatureCategory;
  enforcedBy?: string;
  displayOrder?: number | null;
  showOnPricingPage?: boolean;
  updatedAt?: string;
};

const AUTH = { authenticate: true } as const;

const featureService = {
  list: (params?: FeatureListParams): Promise<FeatureListResult> =>
    apiClient.get(`${API_ENDPOINTS.FEATURES.LIST}${buildQuery(params || {})}`, AUTH),

  getById: (id: string): Promise<FeatureDetail> =>
    apiClient.get(API_ENDPOINTS.FEATURES.GET(id), AUTH),

  create: (payload: CreateFeaturePayload): Promise<Feature> =>
    apiClient.post(API_ENDPOINTS.FEATURES.CREATE, payload, AUTH),

  update: (id: string, payload: UpdateFeaturePayload): Promise<Feature> =>
    apiClient.patch(API_ENDPOINTS.FEATURES.UPDATE(id), payload, AUTH),

  archive: (id: string): Promise<Feature> =>
    apiClient.post(API_ENDPOINTS.FEATURES.ARCHIVE(id), {}, AUTH),

  restore: (id: string): Promise<Feature> =>
    apiClient.post(API_ENDPOINTS.FEATURES.RESTORE(id), {}, AUTH),

  hardDelete: (id: string): Promise<void> =>
    apiClient.delete(API_ENDPOINTS.FEATURES.DELETE(id), AUTH),

  getAuditLog: (id: string, page = 1, page_size = 25): Promise<AuditLogResult> =>
    apiClient.get(`${API_ENDPOINTS.FEATURES.AUDIT_LOG(id)}${buildQuery({ page, page_size })}`, AUTH),

  /** Runtime checks a feature can be enforced by ("Enforced by" options). */
  listHooks: async (): Promise<FeatureHook[]> => {
    const result: any = await apiClient.get(API_ENDPOINTS.FEATURES.HOOKS, AUTH);
    return ((result?.data ?? result)?.hooks ?? []) as FeatureHook[];
  },
};

export type FeatureHook = { value: string; label: string; type: 'boolean' | 'limit' | 'option' | null };

export { featureService };
